import { TFunction } from 'i18next';

import { Space } from '../../vendor/my-spaces/models/space.model';

const norm = (value?: unknown) => (value == null ? '' : String(value)).replace(/ {2,}/g, ' ').toLowerCase();

/**
 * The library search, matching what it always has (_id, id, name, a surface
 * with its underscore read as a space) plus the room type. Null-safe: a space
 * without a name no longer throws while you type.
 */
export function matchesSpaceSearch(space: Space, value: string): boolean {
    const q = value.toLowerCase();
    if (!q) return true;
    return (
        norm(space._id).includes(q) ||
        norm(space.id).includes(q) ||
        norm(space.name).includes(q) ||
        (space.surfaces ?? []).some((area) => norm(area).replace('_', ' ').includes(q)) ||
        norm(space.spaceType).includes(q)
    );
}

/** "Floor", "Walls"… in the active language; an unknown surface reads as itself. */
export const surfaceLabel = (t: TFunction, surface: string) =>
    t(`ADMIN_SPACES.SURFACE.${surface.toUpperCase()}`, { defaultValue: surface.replace(/_/g, ' ') }) as string;

/** How many spaces of each room type, most common first. Spaces without a type are left out. */
export function countByRoomType(spaces: Space[]): { type: string; count: number }[] {
    const counts = new Map<string, number>();
    spaces.forEach((space) => {
        if (space.spaceType) counts.set(space.spaceType, (counts.get(space.spaceType) ?? 0) + 1);
    });
    return Array.from(counts, ([type, count]) => ({ type, count })).sort((a, b) => b.count - a.count || a.type.localeCompare(b.type));
}

/** Every surface kind used across the library (floor, walls…), as today's "surface types" tile counted them. */
export function uniqueSurfaces(spaces: Space[]): string[] {
    const set = new Set<string>();
    spaces.forEach((space) => space.surfaces?.forEach((surface) => set.add(surface)));
    return Array.from(set);
}

/** Added in the last `days` days (by createdAt). */
export function addedWithin(spaces: Space[], days: number, now = Date.now()): number {
    const since = now - days * 24 * 60 * 60 * 1000;
    return spaces.filter((space) => space.createdAt && new Date(space.createdAt).getTime() >= since).length;
}
